import { discountPercent, escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent } from '../types.js';
import {
  ARABIC_LINE,
  bilingual,
  description,
  hasTitle,
  image,
  lineCount,
  price,
  sectionTitle,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * A dark poster: each section under a ribbon label, its items in bracket-framed columns with
 * dotted leaders to the price, and a round plate photo set on bright accent shapes, with a
 * "save" badge when that dish is discounted. Portrait boards zig-zag the photo from side to
 * side; landscape boards set the sections side by side.
 */

/** The photo a block shows: the section's own, or else its first item with one. */
function plateFor(block: MenuBlock): { mediaId: string; item: MenuItemContent | null } | null {
  if (block.continued) return null;
  if (block.section.imageMediaId) return { mediaId: block.section.imageMediaId, item: null };
  const item = block.items.find((i) => i.imageMediaId);
  return item ? { mediaId: item.imageMediaId!, item } : null;
}

function itemPrice(ctx: RenderContext, item: MenuItemContent): string {
  if (item.variants.length) {
    return item.variants
      .map((v) => `<span class="dr-price"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> ${price(ctx, v.price)}</span>`)
      .join('');
  }
  const percent = discountPercent(item);
  // The old price sits under the new one, leaving the width to the dish's name.
  return percent === null
    ? `<span class="dr-price" dir="ltr">${price(ctx, item.price)}</span>`
    : `<span class="dr-pricebox"><span class="dr-price" dir="ltr">${price(ctx, item.discountPrice!)}</span><s class="dr-old" dir="ltr">${price(ctx, item.price)}</s></span>`;
}

function itemRow(ctx: RenderContext, item: MenuItemContent): string {
  const arabicFirst = !ctx.showEn && item.titleAr?.trim();
  const name = arabicFirst
    ? `<span class="dr-name mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr!.trim())}</span>`
    : `<span class="dr-name">${escapeHtml(item.title)}</span>`;
  const second =
    ctx.showEn && ctx.showAr && item.titleAr?.trim()
      ? `<p class="dr-name2 mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr.trim())}</p>`
      : '';
  return [
    '<div class="dr-item">',
    `<div class="dr-line"><span class="dr-dot"></span>${name}<span class="dr-leader"></span>${itemPrice(ctx, item)}</div>`,
    second,
    description(ctx, item, 'dr-desc'),
    '</div>',
  ].join('');
}

function plate(ctx: RenderContext, block: MenuBlock): string {
  const found = plateFor(block);
  if (!found) return '';
  const photo = image(ctx, found.mediaId, 'dr-plate-img');
  if (!photo) return '';
  const percent = found.item ? discountPercent(found.item) : null;
  const badge =
    percent !== null
      ? `<span class="dr-save" dir="ltr"><small>SAVE UP TO</small><b>${percent}%</b><small>OFF</small></span>`
      : '';
  return `<div class="dr-plate">${photo}${badge}</div>`;
}

function ribbon(ctx: RenderContext, block: MenuBlock): string {
  return hasTitle(block.section) ? `<div class="dr-ribbon">${sectionTitle(ctx, block.section, 'dr-rt')}</div>` : '';
}

function band(ctx: RenderContext, block: MenuBlock, index: number): string {
  const photo = plate(ctx, block);
  const half = Math.ceil(block.items.length / 2);
  const columns = [block.items.slice(0, half), block.items.slice(half)].filter((c) => c.length);
  const lists = columns
    .map((items, i) => `<div class="dr-col dr-col-${i ? 'b' : 'a'}">${items.map((it) => itemRow(ctx, it)).join('')}</div>`)
    .join('');
  return [
    `<div class="dr-band${photo ? ` dr-photo-${index % 2 ? 'left' : 'right'}` : ''}">`,
    photo ? `<div class="dr-band-photo">${photo}</div>` : '',
    `<div class="dr-band-body">${ribbon(ctx, block)}<div class="dr-cols">${lists}</div></div>`,
    '</div>',
  ].join('');
}

function stack(ctx: RenderContext, block: MenuBlock): string {
  return [
    '<div class="dr-stack">',
    ribbon(ctx, block),
    plate(ctx, block),
    `<div class="dr-col dr-col-a">${block.items.map((it) => itemRow(ctx, it)).join('')}</div>`,
    '</div>',
  ].join('');
}

function itemHeight(board: MenuBoardContent, item: MenuItemContent, perLine: number): number {
  const en = board.settings.language !== 'ar';
  const ar = board.settings.language !== 'en';
  const desc = board.settings.showDescriptions;
  const wraps = item.title.length > (perLine < 45 ? 14 : 24) ? 1.7 : 0;
  const names = 1.75 + wraps + (en && ar && item.titleAr ? 1.3 * ARABIC_LINE : 0);
  const lines = desc
    ? (en || !item.descriptionAr ? lineCount(item.description, perLine, 2) : 0) +
      (ar ? lineCount(item.descriptionAr, perLine, 2) : 0)
    : 0;
  return names + lines * 1.15 + 1.4 + (item.variants.length > 1 ? 1.2 : 0);
}

function columns(board: MenuBoardContent, portrait: boolean): MenuColumn[] {
  const head = board.settings.language === 'both' ? 6.2 : 4.8;
  if (portrait) {
    return [
      {
        capacity: 112,
        blockCost: (section, items, continued) => {
          const half = Math.ceil(items.length / 2);
          const height = (list: MenuItemContent[]) => list.reduce((s, i) => s + itemHeight(board, i, 34), 0);
          const body = (hasTitle(section) ? head : 0) + Math.max(height(items.slice(0, half)), height(items.slice(half))) + 2.4;
          const hasPhoto = !continued && Boolean(section.imageMediaId || items.some((i) => i.imageMediaId));
          return Math.max(hasPhoto ? 23 : 0, body) + 3.5;
        },
      },
    ];
  }
  const column: MenuColumn = {
    capacity: 62,
    blockCost: (section, items, continued) => {
      const hasPhoto = !continued && Boolean(section.imageMediaId || items.some((i) => i.imageMediaId));
      return (
        (hasTitle(section) ? head : 0) +
        (hasPhoto ? 18 : 0) +
        items.reduce((s, i) => s + itemHeight(board, i, 52), 0) +
        4.5
      );
    },
  };
  return [column, column, column];
}

export const darkRibbon: MenuTemplate = {
  id: 'dark-ribbon',
  name: 'Dark ribbon',
  description:
    'A dark poster with ribbon headings, bracket-framed columns with dotted prices, and round plate photos on bright accent shapes, with “save” badges on deals.',
  defaultTheme: {
    backgroundColor: '#23262c',
    surfaceColor: '#2c3037',
    textColor: '#ffffff',
    primaryColor: '#ffffff',
    accentColor: '#f6b51e',
    fontId: 'modern',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'amber', name: 'Amber', theme: {} },
    { id: 'ember', name: 'Ember', theme: { backgroundColor: '#1d2128', surfaceColor: '#262b33', accentColor: '#f39a2b', fontId: 'cafe' } },
    { id: 'chili', name: 'Chili', theme: { backgroundColor: '#171717', surfaceColor: '#222222', accentColor: '#e63946', fontId: 'bold' } },
    { id: 'basil', name: 'Basil', theme: { backgroundColor: '#1d221b', surfaceColor: '#262d23', accentColor: '#9ccf4a', fontId: 'modern' } },
  ],
  cuisines: ['burgers', 'pizza'],
  uses: { featured: false, sectionImages: true, brand: false, decorations: [] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    return packColumns(groupBySection(board), columns(board, portrait), { balance: !portrait });
  },

  render(ctx) {
    if (ctx.orientation === 'portrait') {
      const blocks = ctx.page.columns.flat();
      return `<div class="dr-bands">${blocks.map((b, i) => band(ctx, b, i)).join('')}</div>`;
    }
    return `<div class="dr-row">${ctx.page.columns
      .map((column) => `<div class="dr-column">${column.map((b) => stack(ctx, b)).join('')}</div>`)
      .join('')}</div>`;
  },

  css: `
.mb-t-dark-ribbon{padding:3em 3.2em;
  background-image:radial-gradient(circle at 85% 8%,rgba(255,255,255,.05),transparent 35%),
    radial-gradient(rgba(255,255,255,.05) .09em,transparent .1em);
  background-size:auto,1.4em 1.4em}
.mb-t-dark-ribbon .dr-bands{flex:1;min-height:0;display:flex;flex-direction:column;justify-content:space-evenly;gap:3em}
.mb-t-dark-ribbon .dr-band{display:grid;grid-template-columns:1fr;gap:2.5em;align-items:center}
.mb-t-dark-ribbon .dr-photo-right{grid-template-columns:1fr 23em}
.mb-t-dark-ribbon .dr-photo-left{grid-template-columns:23em 1fr}
.mb-t-dark-ribbon .dr-photo-right .dr-band-photo{order:2}
.mb-t-dark-ribbon .dr-band-body{min-width:0;display:flex;flex-direction:column;align-items:center;gap:1.4em}
.mb-t-dark-ribbon .dr-cols{width:100%;display:grid;grid-template-columns:repeat(auto-fit,minmax(0,1fr));gap:2.4em}
.mb-t-dark-ribbon .dr-row{flex:1;min-height:0;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:3em}
.mb-t-dark-ribbon .dr-column{min-width:0;display:flex;flex-direction:column;justify-content:center;gap:2.4em}
.mb-t-dark-ribbon .dr-stack{display:flex;flex-direction:column;align-items:center;gap:1.4em}
.mb-t-dark-ribbon .dr-stack .dr-plate{width:19em;margin:1em 0 .6em}
.mb-t-dark-ribbon .dr-stack .dr-col{width:100%}

.mb-t-dark-ribbon .dr-ribbon{background:var(--mb-accent);color:var(--mb-bg);padding:.35em 1.6em .3em;text-align:center;
  clip-path:polygon(5% 0,95% 0,100% 50%,95% 100%,5% 100%,0 50%)}
.mb-t-dark-ribbon .dr-rt{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2.1em;
  text-transform:uppercase;line-height:1.1;letter-spacing:.02em}
.mb-t-dark-ribbon .dr-rt.mb-2nd{font-size:1.4em}
.mb-t-dark-ribbon.mb-v-ember .dr-ribbon{clip-path:polygon(0 0,100% 0,97% 100%,3% 100%);padding:.35em 2em .3em;
  background:linear-gradient(90deg,var(--mb-accent),color-mix(in srgb,var(--mb-accent) 70%,#fff))}
.mb-t-dark-ribbon.mb-v-chili .dr-ribbon{clip-path:none;border-radius:99em}
.mb-t-dark-ribbon.mb-v-basil .dr-ribbon{clip-path:polygon(0 0,100% 0,100% 70%,94% 100%,6% 100%,0 70%)}

.mb-t-dark-ribbon .dr-col{position:relative;padding:1.2em 1.6em 1.4em;display:flex;flex-direction:column;gap:1.2em}
.mb-t-dark-ribbon .dr-col:before{content:'';position:absolute;inset:0;pointer-events:none;border:.13em solid var(--mb-accent)}
.mb-t-dark-ribbon .dr-col-a:before{border-top:0;border-left:0;border-bottom-right-radius:1.6em;
  -webkit-mask:linear-gradient(90deg,transparent 30%,#000 60%);mask:linear-gradient(90deg,transparent 30%,#000 60%)}
.mb-t-dark-ribbon .dr-col-b:before{border-right:0;border-bottom:0;border-top-left-radius:1.6em;
  -webkit-mask:linear-gradient(270deg,transparent 30%,#000 60%);mask:linear-gradient(270deg,transparent 30%,#000 60%)}
.mb-t-dark-ribbon.mb-v-ember .dr-col:before{border-radius:1.6em}
.mb-t-dark-ribbon[dir=rtl] .dr-col-a:before{transform:scaleX(-1)}
.mb-t-dark-ribbon[dir=rtl] .dr-col-b:before{transform:scaleX(-1)}

.mb-t-dark-ribbon .dr-line{display:flex;align-items:last baseline;gap:.55em}
.mb-t-dark-ribbon .dr-dot{flex:0 0 .45em;height:.45em;border-radius:50%;background:var(--mb-text);transform:translateY(-.2em)}
.mb-t-dark-ribbon .dr-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.45em;
  color:var(--mb-accent);line-height:1.15;flex:0 1 auto;min-width:min(55%,7em);
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.mb-t-dark-ribbon.mb-v-ember .dr-name,.mb-t-dark-ribbon.mb-v-chili .dr-name{text-transform:uppercase}
.mb-t-dark-ribbon .dr-name2{font-weight:700;color:var(--mb-accent);opacity:.9;margin:.1em 0 0 1em}
.mb-t-dark-ribbon .dr-leader{flex:1 0 1em;min-width:1em;border-bottom:.17em dotted color-mix(in srgb,var(--mb-text) 55%,transparent);transform:translateY(-.3em)}
.mb-t-dark-ribbon .dr-price{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.45em;
  color:var(--mb-text);white-space:nowrap}
.mb-t-dark-ribbon .dr-price small{font-size:.55em;opacity:.75;margin-inline-end:.15em}
.mb-t-dark-ribbon .dr-price+.dr-price{margin-inline-start:.5em}
.mb-t-dark-ribbon .dr-pricebox{display:flex;flex-direction:column;align-items:flex-end;line-height:1.05}
.mb-t-dark-ribbon .dr-old{font-size:.85em;opacity:.5;white-space:nowrap}
.mb-t-dark-ribbon .dr-desc{font-size:.85em;opacity:.72;margin:.25em 0 0 1.05em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}

.mb-t-dark-ribbon .dr-band-photo{display:flex;justify-content:center}
.mb-t-dark-ribbon .dr-plate{position:relative;width:21em;aspect-ratio:1}
.mb-t-dark-ribbon .dr-plate:before,.mb-t-dark-ribbon .dr-plate:after{content:'';position:absolute;z-index:0;
  inset:12% -9%;background:var(--mb-accent);border-radius:.5em;transform:rotate(28deg)}
.mb-t-dark-ribbon .dr-plate:after{transform:rotate(-28deg)}
.mb-t-dark-ribbon.mb-v-ember .dr-plate:before{inset:-7%;background:none;border:.45em solid var(--mb-accent);border-radius:50%;
  transform:none;clip-path:inset(45% 0 0 45%)}
.mb-t-dark-ribbon.mb-v-ember .dr-plate:after{inset:auto -10% -10% auto;width:45%;height:45%;border-radius:50%;transform:none}
.mb-t-dark-ribbon.mb-v-chili .dr-plate:before{inset:-5%;border-radius:50%;transform:none;opacity:.9}
.mb-t-dark-ribbon.mb-v-chili .dr-plate:after{display:none}
.mb-t-dark-ribbon.mb-v-basil .dr-plate:before{inset:4% -8% 4% 30%;border-radius:1.2em;transform:rotate(8deg)}
.mb-t-dark-ribbon.mb-v-basil .dr-plate:after{display:none}
.mb-t-dark-ribbon .dr-plate-img{position:relative;z-index:1;width:100%;height:100%;object-fit:cover;border-radius:50%;
  box-shadow:0 0 0 .5em rgba(0,0,0,.35),0 1.2em 2.4em rgba(0,0,0,.5)}
/* Portrait bands share the width with a plate, so they set the list smaller, as print menus do. */
.mb-t-dark-ribbon.mb-portrait .dr-photo-right{grid-template-columns:1fr 19em}
.mb-t-dark-ribbon.mb-portrait .dr-photo-left{grid-template-columns:19em 1fr}
.mb-t-dark-ribbon.mb-portrait .dr-band .dr-plate{width:18em}
.mb-t-dark-ribbon.mb-portrait .dr-band .dr-cols{gap:1.4em}
.mb-t-dark-ribbon.mb-portrait .dr-band .dr-col{padding:1em 1.1em 1.2em}
.mb-t-dark-ribbon.mb-portrait .dr-band .dr-name{font-size:1.2em;min-width:0}
.mb-t-dark-ribbon.mb-portrait .dr-band .dr-price{font-size:1.2em}
.mb-t-dark-ribbon.mb-portrait .dr-band .dr-desc{font-size:.78em}
.mb-t-dark-ribbon.mb-portrait .dr-band .dr-leader{flex-basis:.8em;min-width:.8em}
.mb-t-dark-ribbon .dr-save{position:absolute;z-index:2;top:-2%;right:4%;width:5.6em;height:5.6em;border-radius:50%;
  background:var(--mb-accent);color:var(--mb-bg);display:flex;flex-direction:column;align-items:center;justify-content:center;
  box-shadow:0 .4em 1em rgba(0,0,0,.4);line-height:1}
.mb-t-dark-ribbon .dr-save small{font-size:.6em;font-weight:700;letter-spacing:.04em}
.mb-t-dark-ribbon .dr-save b{font-family:var(--mb-font-heading);font-size:1.9em;margin:.05em 0}
`,
};
